/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import useOnClickOutside from '../../../../hooks/useOnClickOutside';
import usePaginatedDropdownOptions from '../../../../hooks/usePaginatedDropdownOptions';
import classes from './styles.module.scss';

export default function FilterDropdown({
  options,
  value,
  placeholder,
  label,
  name,
  setFieldValue,
  fetchOptions,
  readonly,
  multiSelect = false,
  staticPrependOptions, // Static options to show at top (above fetched/static options)
}) {
  const [isOptionsListVisible, setIsOptionsListVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const dropdownRef = useRef();
  const optionsRef = useRef();
  const selectorRef = useRef();

  useOnClickOutside(
    optionsRef,
    () => setIsOptionsListVisible(false),
    selectorRef
  );

  const setOption = (option) => {
    if (multiSelect) {
      // Multi-select: toggle option in array
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.some((v) => v.value === option.value);

      if (isSelected) {
        setFieldValue(
          name,
          currentValues.filter((v) => v.value !== option.value)
        );
      } else {
        setFieldValue(name, [...currentValues, option]);
      }
    } else {
      // Single select: set value and close
      setFieldValue(name, option);
      setIsOptionsListVisible(false);
    }
  };

  useEffect(() => {
    if (!isOptionsListVisible) {
      setSearchTerm('');
    }
  }, [isOptionsListVisible]);

  const { options: fetchedOptions, lastListElementRef } =
    usePaginatedDropdownOptions({
      dataName: fetchOptions?.dataName,
      getDataHandler: fetchOptions?.getDataHandler,
      searchTerm,
      labelName: fetchOptions?.labelName,
      isUserList: fetchOptions?.isUserList,
      isJobPostsPicker: fetchOptions?.isJobPostsPicker,
      isCompanyList: fetchOptions?.isCompanyList,
      isExternalUserList: fetchOptions?.isExternalUserList,
      fetchHanlderOptions: fetchOptions?.fetchHanlderOptions,
    });

  // Use fetched options if available, otherwise use static options
  const displayedOptions = fetchOptions ? fetchedOptions : options;

  // Debug logging
  if (name?.includes('language')) {
    // eslint-disable-next-line no-console
    console.log('🔍 FilterDropdown Debug:', {
      name,
      hasFetchOptions: !!fetchOptions,
      staticOptions: options?.length || 0,
      fetchedOptions: fetchedOptions?.length || 0,
      displayedOptions: displayedOptions?.length || 0,
    });
  }

  const baseFilteredOptions =
    searchTerm && !fetchOptions
      ? (displayedOptions || []).filter((opt) =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : displayedOptions || [];

  // Prepend static options (always visible, not affected by search)
  const filteredOptions = staticPrependOptions
    ? [...staticPrependOptions, ...baseFilteredOptions]
    : baseFilteredOptions;

  // Debug filtered options
  if (name?.includes('language') && isOptionsListVisible) {
    // eslint-disable-next-line no-console
    console.log(
      '📋 Filtered options for',
      name,
      ':',
      filteredOptions?.length || 0
    );
    if (filteredOptions?.length > 0) {
      // eslint-disable-next-line no-console
      console.log('First 3 options:', filteredOptions.slice(0, 3));
    }
  }

  // Helper to get display text
  const getDisplayText = () => {
    if (multiSelect) {
      if (!value || value.length === 0) return placeholder;
      if (value.length === 1) return value[0].label;
      return `${value.length} selected`;
    }
    return value ? value.label : placeholder;
  };

  return (
    <div className={classes.FilterDropdown} ref={dropdownRef}>
      {label && (
        <label htmlFor={name} className={classes.label}>
          {label}
        </label>
      )}
      <div
        ref={selectorRef}
        className={classNames(classes.selector, {
          [classes.open]: isOptionsListVisible,
          [classes.readonly]: readonly,
        })}
        onClick={() => {
          if (readonly) return;
          setIsOptionsListVisible((prev) => !prev);
        }}
      >
        <span
          className={classNames(classes.placeholder, {
            [classes.hasValue]: multiSelect ? value?.length > 0 : value,
          })}
        >
          {getDisplayText()}
        </span>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={classNames(classes.arrow, {
            [classes.open]: isOptionsListVisible,
          })}
        >
          <path
            d="M8 10L12 14L16 10"
            stroke="#242833"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {isOptionsListVisible && (
        <div className={classes.optionsContainer} ref={optionsRef}>
          {(fetchOptions || options?.length > 10) && (
            <div className={classes.searchBox}>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classes.searchInput}
              />
            </div>
          )}
          <ul className={classes.optionsList}>
            {filteredOptions.map((option, index) => {
              const isLast = index === filteredOptions.length - 1;
              const isSelected = multiSelect
                ? Array.isArray(value) &&
                  value.some((v) => v.value === option.value)
                : value?.value === option.value;

              return (
                <li
                  key={option.value}
                  ref={isLast ? lastListElementRef : null}
                  className={classNames(classes.option, {
                    [classes.selected]: isSelected,
                  })}
                  onClick={() => setOption(option)}
                >
                  {multiSelect && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className={classes.checkbox}
                    />
                  )}
                  {option.label}
                </li>
              );
            })}
            {filteredOptions.length === 0 && (
              <li className={classes.noResults}>No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
